<template>
  <div class="resumen-paises-v2">
    <div class="rp-header">
      <div>
        <div class="rp-eyebrow">SALES BREAKDOWN</div>
        <h3 class="rp-title">Resumen por país</h3>
      </div>
      <div class="rp-actions">
        <div class="rp-seg">
          <button type="button" class="rp-seg-btn" :class="{ active: vista === 'pedidos' }" @click="vista = 'pedidos'">
            <i class="fa-solid fa-cart-shopping"></i>
            <span>Pedidos</span>
          </button>
          <button type="button" class="rp-seg-btn" :class="{ active: vista === 'dinero' }" @click="vista = 'dinero'">
            <i class="fa-solid fa-pound-sign"></i>
            <span>Ingresos</span>
          </button>
          <button type="button" class="rp-seg-btn" :class="{ active: vista === 'combo' }" @click="vista = 'combo'">
            <i class="fa-solid fa-table-columns"></i>
            <span>Ambos</span>
          </button>
        </div>
        <button class="rp-btn-csv" @click="descargarCSVActual" :title="'Descargar CSV'">
          <i class="fa-solid fa-download"></i>
          <span>CSV</span>
        </button>
      </div>
    </div>

    <div class="rp-table-wrap">
      <table class="rp-table">
        <thead>
          <tr>
            <th class="th-rank">#</th>
            <th>PAÍS</th>
            <template v-if="vista === 'pedidos' || vista === 'combo'">
              <th class="th-right">PEDIDOS</th>
              <th class="th-right">AÑO ANT.</th>
              <th class="th-right">Δ %</th>
            </template>
            <template v-if="vista === 'dinero' || vista === 'combo'">
              <th class="th-right">INGRESOS</th>
              <th class="th-right">AÑO ANT.</th>
              <th class="th-right">Δ %</th>
            </template>
          </tr>
        </thead>
        <tbody>
          <tr
            v-for="(row, idx) in filasUnificadas"
            :key="row.nombre"
            :class="{ 'is-total': row.nombre === 'TOTAL GLOBAL' }"
          >
            <td class="td-rank mono">{{ row.nombre === 'TOTAL GLOBAL' ? '★' : String(idx).padStart(2, '0') }}</td>
            <td class="td-pais">
              <span class="flag">{{ getFlag(row.nombre) }}</span>
              <span class="pais-name">{{ row.nombre }}</span>
            </td>
            <template v-if="vista === 'pedidos' || vista === 'combo'">
              <td class="td-right mono td-num">{{ formatearValor(row.pedidos.actual, 'cantidad') }}</td>
              <td class="td-right mono td-prev">{{ formatearValor(row.pedidos.promedio, 'cantidad') }}</td>
              <td class="td-right">
                <span class="delta" :class="deltaClass(row.pedidos.diferencia)">
                  {{ deltaIcon(row.pedidos.diferencia) }} {{ formatDelta(row.pedidos.diferencia) }}
                </span>
              </td>
            </template>
            <template v-if="vista === 'dinero' || vista === 'combo'">
              <td class="td-right mono td-num">£{{ formatearValor(row.dinero.actual, 'dinero') }}</td>
              <td class="td-right mono td-prev">£{{ formatearValor(row.dinero.promedio, 'dinero') }}</td>
              <td class="td-right">
                <span class="delta" :class="deltaClass(row.dinero.diferencia)">
                  {{ deltaIcon(row.dinero.diferencia) }} {{ formatDelta(row.dinero.diferencia) }}
                </span>
              </td>
            </template>
          </tr>
          <tr v-if="filasUnificadas.length === 0">
            <td :colspan="vista === 'combo' ? 8 : 5" class="td-empty">Sin datos en el rango seleccionado</td>
          </tr>
        </tbody>
      </table>
    </div>
  </div>
</template>

<script>
import ResumenPaisCard from "./cardPais.vue";

export default {
  components: {
    ResumenPaisCard,
  },
  data() {
    return {
      resumenPorPais: [],
      orders: [],
      ordersOlds: [],
      resumenPorPaisDinero: [],
      vista: 'combo',
    };
  },
  props: ["items", "itemsOld", "fechaDesde", "fechaHasta"],
  methods: {
    recalcular() {
      if (!this.orders || !this.orders.length) return;
      this.calcularResumenPaisPorDinero();
      this.calcularResumenPorNumeroDeOrdenes();
    },
    getColor(pais) {
      switch (pais.estado) {
        case "ok":
          return "success";
        case "advertencia":
          return "warning";
        case "bajo":
          return "danger";
        default:
          return "secondary";
      }
    },
    calcularResumenPorNumeroDeOrdenes() {
      const ordenesPorPais = {};
      const ordenesHistoricas = {};

      const hoy = this.fechaHasta ? new Date(this.fechaHasta + 'T23:59:59') : new Date();
      const hace15Dias = this.fechaDesde ? new Date(this.fechaDesde + 'T00:00:00') : new Date(hoy.getTime() - 15 * 86400000);

      this.orders.forEach((order) => {
        const pais = order.shipping_address?.country || "Desconocido";
        const fecha = new Date(order.created_at);

        // Actual (últimos 15 días)
        if (fecha >= hace15Dias && fecha <= hoy) {
          if (!ordenesPorPais[pais]) ordenesPorPais[pais] = 0;
          ordenesPorPais[pais] += 1;
        }

        // Histórico: mismo rango en años anteriores
        for (let offset = 1; offset <= 5; offset++) {
          const historicoInicio = new Date(hace15Dias);
          historicoInicio.setFullYear(hace15Dias.getFullYear() - offset);

          const historicoFin = new Date(hoy);
          historicoFin.setFullYear(hoy.getFullYear() - offset);

          if (fecha >= historicoInicio && fecha <= historicoFin) {
            if (!ordenesHistoricas[pais]) ordenesHistoricas[pais] = {};
            if (!ordenesHistoricas[pais][offset]) ordenesHistoricas[pais][offset] = 0;

            ordenesHistoricas[pais][offset] += 1;
          }
        }
      });

      const resumen = [];
      let totalActual = 0;
      let totalPromedio = 0;

      Object.keys(ordenesPorPais).forEach((pais) => {
        const historico = ordenesHistoricas[pais] || {};
        const totalHistorico = Object.values(historico).reduce(
          (acc, val) => acc + val,
          0
        );
        const cantidadAnios = Object.keys(historico).length;
        const promedio = cantidadAnios
          ? Math.round(totalHistorico / cantidadAnios)
          : 0;
        const actual = ordenesPorPais[pais];

        const estado =
          actual >= promedio
            ? "ok"
            : actual >= promedio * 0.8
            ? "advertencia"
            : "bajo";

        const diferencia =
          promedio > 0 ? ((actual - promedio) / promedio) * 100 : 0;

        resumen.push({ nombre: pais, actual, promedio, estado, diferencia });

        totalActual += actual;
        totalPromedio += promedio;
      });

      resumen.sort((a, b) => b.actual - a.actual);

      // Fila TOTAL
      const estadoTotal =
        totalActual >= totalPromedio
          ? "ok"
          : totalActual >= totalPromedio * 0.8
          ? "advertencia"
          : "bajo";

      const diferenciaTotal =
        totalPromedio > 0
          ? ((totalActual - totalPromedio) / totalPromedio) * 100
          : 0;

      const filaTotal = {
        nombre: "TOTAL GLOBAL",
        actual: totalActual,
        promedio: totalPromedio,
        estado: estadoTotal,
        diferencia: diferenciaTotal,
      };

      this.resumenPorPais = [filaTotal, ...resumen];
    },

    calcularResumenPais() {
      const ventasPorPais = {};
      const ventasHistoricas = {}; // agrupadas por año

      const hoy = this.fechaHasta ? new Date(this.fechaHasta + 'T23:59:59') : new Date();
      const hace15Dias = this.fechaDesde ? new Date(this.fechaDesde + 'T00:00:00') : new Date(hoy.getTime() - 15 * 86400000);

      this.orders.forEach((order) => {
        const pais = order.shipping_address?.country || "Desconocido";
        const fecha = new Date(order.created_at);
        const anio = fecha.getFullYear();

        // Actual
        if (fecha >= hace15Dias && fecha <= hoy) {
          if (!ventasPorPais[pais]) ventasPorPais[pais] = 0;
          const totalItems = order.line_items.reduce(
            (acc, item) => acc + item.quantity,
            0
          );
          ventasPorPais[pais] += totalItems;
        }

        // Histórico: mismo rango en años anteriores
        for (let offset = 1; offset <= 5; offset++) {
          // compara hasta 5 años atrás
          const historicoInicio = new Date(hace15Dias);
          const historicoFin = new Date(hoy);
          historicoInicio.setFullYear(hoy.getFullYear() - offset);
          historicoFin.setFullYear(hoy.getFullYear() - offset);

          if (fecha >= historicoInicio && fecha <= historicoFin) {
            if (!ventasHistoricas[pais]) ventasHistoricas[pais] = {};
            if (!ventasHistoricas[pais][offset])
              ventasHistoricas[pais][offset] = 0;

            const totalItems = order.line_items.reduce(
              (acc, item) => acc + item.quantity,
              0
            );
            ventasHistoricas[pais][offset] += totalItems;
          }
        }
      });

      const resumen = [];

      Object.keys(ventasPorPais).forEach((pais) => {
        const historico = ventasHistoricas[pais] || {};
        const totalHistorico = Object.values(historico).reduce(
          (acc, val) => acc + val,
          0
        );
        const cantidadAnios = Object.keys(historico).length;
        const promedio = cantidadAnios
          ? Math.round(totalHistorico / cantidadAnios)
          : 0;
        const actual = ventasPorPais[pais];

        const estado =
          actual >= promedio
            ? "ok"
            : actual >= promedio * 0.8
            ? "advertencia"
            : "bajo";

        const diferencia =
          promedio > 0 ? ((actual - promedio) / promedio) * 100 : 0;

        resumen.push({ nombre: pais, actual, promedio, estado, diferencia });
      });

      this.resumenPorPais = resumen;
    },
    calcularResumenPaisPorDinero() {
      const ventasPorPais = {};
      const ventasHistoricas = {}; // agrupadas por año

      const hoy = this.fechaHasta ? new Date(this.fechaHasta + 'T23:59:59') : new Date();
      const hace15Dias = this.fechaDesde ? new Date(this.fechaDesde + 'T00:00:00') : new Date(hoy.getTime() - 15 * 86400000);

      this.orders.forEach((order) => {
        const pais = order.shipping_address?.country || "Desconocido";
        const fecha = new Date(order.created_at);
        const valorOrden = parseFloat(order.total) || 0;

        // Actual (últimos 15 días)
        if (fecha >= hace15Dias && fecha <= hoy) {
          if (!ventasPorPais[pais]) ventasPorPais[pais] = 0;
          ventasPorPais[pais] += valorOrden;
        }

        // Histórico: mismo rango en años anteriores
        for (let offset = 1; offset <= 5; offset++) {
          const historicoInicio = new Date(hace15Dias);
          historicoInicio.setFullYear(hace15Dias.getFullYear() - offset);

          const historicoFin = new Date(hoy);
          historicoFin.setFullYear(hoy.getFullYear() - offset);

          if (fecha >= historicoInicio && fecha <= historicoFin) {
            if (!ventasHistoricas[pais]) ventasHistoricas[pais] = {};
            if (!ventasHistoricas[pais][offset])
              ventasHistoricas[pais][offset] = 0;

            ventasHistoricas[pais][offset] += valorOrden;
          }
        }
      });

      const resumen = [];
      let totalActual = 0;
      let totalPromedio = 0;

      Object.keys(ventasPorPais).forEach((pais) => {
        const historico = ventasHistoricas[pais] || {};
        const totalHistorico = Object.values(historico).reduce(
          (acc, val) => acc + val,
          0
        );
        const cantidadAnios = Object.keys(historico).length;        
        const promedio = cantidadAnios
          ? Math.round(totalHistorico / cantidadAnios)
          : 0;
        const actual = ventasPorPais[pais];

        resumen.sort((a, b) => b.actual - a.actual);

        const estado =
          actual >= promedio
            ? "ok"
            : actual >= promedio * 0.8
            ? "advertencia"
            : "bajo";

        const diferencia =
          promedio > 0 ? ((actual - promedio) / promedio) * 100 : 0;

        resumen.push({ nombre: pais, actual, promedio, estado, diferencia });

        totalActual += actual;
        totalPromedio += promedio;
      });

      // Fila TOTAL
      const estadoTotal =
        totalActual >= totalPromedio
          ? "ok"
          : totalActual >= totalPromedio * 0.8
          ? "advertencia"
          : "bajo";

      const diferenciaTotal =
        totalPromedio > 0
          ? ((totalActual - totalPromedio) / totalPromedio) * 100
          : 0;

      const filaTotal = {
        nombre: "TOTAL GLOBAL",
        actual: totalActual,
        promedio: totalPromedio,
        estado: estadoTotal,
        diferencia: diferenciaTotal,
      };
    
      this.resumenPorPaisDinero = [filaTotal, ...resumen];
    },
    descargarCSVPedidos() {
      const csvContent =
        "data:text/csv;charset=utf-8,\uFEFF" +
        "Pais,Pedidos,Pedidos Año anterior,Diferencia %\n" +
        this.resumenPorPais
          .map(
            (p) =>
              `${p.nombre},${p.actual},${p.promedio},${p.diferencia.toFixed(
                1
              )}%`
          )
          .join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "resumen_pedidos.csv");
      document.body.appendChild(link);
      link.click();
    },
    descargarCSVDinero() {
      const csvContent =
        "data:text/csv;charset=utf-8,\uFEFF" +
        "Pais,Valor,Valor Año anterior,Diferencia %\n" +
        this.resumenPorPaisDinero
          .map(
            (p) =>
              `${p.nombre},${this.formatearValor(
                p.actual,
                "dinero"
              )},${this.formatearValor(
                p.promedio,
                "dinero"
              )},${p.diferencia.toFixed(1)}%`
          )
          .join("\n");
      const encodedUri = encodeURI(csvContent);
      const link = document.createElement("a");
      link.setAttribute("href", encodedUri);
      link.setAttribute("download", "resumen_dinero.csv");
      document.body.appendChild(link);
      link.click();
    },
    formatearValor(valor, modo) {
      if (modo === "dinero") {
        return (Number(valor) || 0).toLocaleString("en-GB", {
          minimumFractionDigits: 2,
          maximumFractionDigits: 2,
        });
      } else {
        return Math.round(Number(valor) || 0).toLocaleString("en-GB");
      }
    },
    getFlag(pais) {
      const flags = {
        "United States": "🇺🇸",
        "United Kingdom": "🇬🇧",
        Germany: "🇩🇪",
        Italy: "🇮🇹",
        France: "🇫🇷",
        Spain: "🇪🇸",
        "TOTAL GLOBAL": "🌐",
      };
      return flags[pais] || "🏳️";
    },
    deltaClass(diferencia) {
      if (diferencia > 0) return "delta-up";
      if (diferencia >= -20) return "delta-flat";
      return "delta-down";
    },
    deltaIcon(diferencia) {
      if (diferencia > 0) return "▲";
      if (diferencia >= -20) return "▶";
      return "▼";
    },
    formatDelta(diferencia) {
      const v = Number(diferencia) || 0;
      const sign = v > 0 ? "+" : "";
      return sign + v.toFixed(1) + "%";
    },
    descargarCSVActual() {
      if (this.vista === "dinero") this.descargarCSVDinero();
      else this.descargarCSVPedidos();
    },
  },
  watch: {
    items: {
      handler() {
        this.orders = this.items;
        this.recalcular();
      },
      immediate: true,
    },
  },
  computed: {
    resumenFiltrado() {
      const paisesPermitidos = [
        "United States",
        "United Kingdom",
        "Germany",
        "Italy",
        "France",
        "Spain",
        "TOTAL GLOBAL",
      ];
      return this.resumenPorPais.filter((p) =>
        paisesPermitidos.includes(p.nombre)
      );
    },
    resumenFiltradoDinero() {
      const paisesPermitidos = [
        "United States",
        "United Kingdom",
        "Germany",
        "Italy",
        "France",
        "Spain",
        "TOTAL GLOBAL",
      ];
      return this.resumenPorPaisDinero.filter((p) =>
        paisesPermitidos.includes(p.nombre)
      );
    },
    filasUnificadas() {
      const dineroByName = {};
      this.resumenFiltradoDinero.forEach((p) => {
        dineroByName[p.nombre] = p;
      });

      const total = this.resumenFiltrado.find((p) => p.nombre === "TOTAL GLOBAL");
      const sinTotal = this.resumenFiltrado
        .filter((p) => p.nombre !== "TOTAL GLOBAL")
        .sort((a, b) => b.actual - a.actual);

      const ordered = total ? [total, ...sinTotal] : sinTotal;

      return ordered.map((p) => {
        const d = dineroByName[p.nombre] || { actual: 0, promedio: 0, diferencia: 0 };
        return {
          nombre: p.nombre,
          pedidos: { actual: p.actual, promedio: p.promedio, diferencia: p.diferencia },
          dinero: { actual: d.actual, promedio: d.promedio, diferencia: d.diferencia },
        };
      });
    },
  },
};
</script>
  
<style lang="scss" scoped>
@import url('https://fonts.googleapis.com/css2?family=Plus+Jakarta+Sans:wght@300;400;500;600;700;800&family=JetBrains+Mono:wght@400;500;600;700&display=swap');

$primary: #3B82F6;
$primary-soft: #60A5FA;
$cta: #F97316;
$bg: #F8FAFC;
$surface: #FFFFFF;
$border: #E2E8F0;
$text: #1E293B;
$text-mute: #64748B;
$text-soft: #94A3B8;
$success: #10B981;
$danger: #EF4444;
$warn: #F59E0B;

.resumen-paises-v2 {
  font-family: 'Plus Jakarta Sans', -apple-system, BlinkMacSystemFont, sans-serif;
  background: $surface;
  border: 1px solid $border;
  border-radius: 14px;
  overflow: hidden;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
  color: $text;
  max-width: 1180px;
  margin: 0 auto;

  .mono {
    font-family: 'JetBrains Mono', monospace;
    font-feature-settings: "tnum", "zero";
  }
}

.rp-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: 16px;
  padding: 16px 20px;
  border-bottom: 1px solid $border;
  background: $bg;
  flex-wrap: wrap;
}

.rp-eyebrow {
  font-family: 'JetBrains Mono', monospace;
  font-size: 10px;
  font-weight: 600;
  color: $primary;
  letter-spacing: 0.14em;
  margin-bottom: 4px;
}

.rp-title {
  font-size: 18px;
  font-weight: 700;
  margin: 0;
  color: $text;
  letter-spacing: -0.01em;
}

.rp-actions {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  flex-wrap: wrap;
}

.rp-seg {
  display: inline-flex;
  background: $surface;
  border: 1px solid $border;
  border-radius: 8px;
  padding: 3px;
}

.rp-seg-btn {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 6px 12px;
  font-size: 12px;
  font-weight: 500;
  font-family: inherit;
  color: $text-mute;
  background: transparent;
  border: 0;
  border-radius: 6px;
  cursor: pointer;
  transition: all 200ms ease;

  i { font-size: 11px; }

  &:hover { color: $text; }

  &.active {
    background: $bg;
    color: $primary;
    box-shadow: 0 1px 2px rgba(15, 23, 42, 0.08);
  }

  &:focus-visible {
    outline: 2px solid $primary;
    outline-offset: 2px;
  }
}

.rp-btn-csv {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 7px 14px;
  font-size: 12px;
  font-weight: 600;
  font-family: inherit;
  color: $surface;
  background: $primary;
  border: 0;
  border-radius: 8px;
  cursor: pointer;
  transition: all 200ms ease;

  &:hover {
    background: darken($primary, 6%);
    box-shadow: 0 4px 12px rgba(59, 130, 246, 0.25);
  }
  &:focus-visible { outline: 2px solid $primary; outline-offset: 2px; }
}

// ===== TABLA =====
.rp-table-wrap {
  overflow-x: auto;
}

.rp-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;

  thead th {
    font-family: 'JetBrains Mono', monospace;
    font-size: 10px;
    font-weight: 600;
    color: $text-soft;
    letter-spacing: 0.08em;
    text-align: left;
    padding: 12px 16px;
    background: $surface;
    border-bottom: 1px solid $border;
    white-space: nowrap;
    position: sticky;
    top: 0;
  }

  tbody tr {
    border-bottom: 1px solid $border;
    transition: background 150ms ease;

    &:hover { background: rgba(59, 130, 246, 0.04); }
    &:last-child { border-bottom: 0; }

    &.is-total {
      background: linear-gradient(90deg, rgba(59, 130, 246, 0.06), rgba(249, 115, 22, 0.04));
      border-bottom: 2px solid $border;
      font-weight: 700;

      .pais-name { font-weight: 700; color: $primary; }
      .td-num { color: $text; font-weight: 700; }
    }
  }

  td {
    padding: 10px 14px;
    color: $text;
    vertical-align: middle;
  }

  thead th { padding: 11px 14px; }
}

.rp-table .th-rank,
.rp-table .td-rank { width: 40px; color: $text-soft; }
.rp-table .th-right,
.rp-table .td-right { text-align: right; }

.td-pais {
  display: flex;
  align-items: center;
  gap: 10px;
}

.flag {
  font-size: 16px;
  line-height: 1;
}

.pais-name {
  font-size: 13px;
  font-weight: 500;
  color: $text;
}

.td-num {
  font-size: 13px;
  font-weight: 600;
  color: $text;
}

.td-prev {
  font-size: 11.5px;
  color: $text-mute;
}

.delta {
  display: inline-flex;
  align-items: center;
  gap: 4px;
  font-family: 'JetBrains Mono', monospace;
  font-size: 11px;
  font-weight: 700;
  padding: 3px 8px;
  border-radius: 12px;
  white-space: nowrap;

  &.delta-up { color: $success; background: rgba(16, 185, 129, 0.1); }
  &.delta-flat { color: $warn; background: rgba(245, 158, 11, 0.1); }
  &.delta-down { color: $danger; background: rgba(239, 68, 68, 0.1); }
}

.td-empty {
  text-align: center;
  padding: 30px;
  color: $text-soft;
  font-size: 12px;
}

@media (prefers-reduced-motion: reduce) {
  *, *::before, *::after {
    animation-duration: 0.01ms !important;
    transition-duration: 0.01ms !important;
  }
}

// ===== DARK MODE =====
.dark-layout {
  .resumen-paises-v2 {
    background: #242424;
    border-color: #3b3b3b;
    color: #d0d2d6;
  }
  .rp-header { background: #1f1f1f; border-bottom-color: #3b3b3b; }
  .rp-title { color: #e4e6eb; }
  .rp-seg { background: #1f1f1f; border-color: #3b3b3b; }
  .rp-seg-btn.active { background: #2c2c2c; }

  .rp-table thead th {
    background: #242424;
    border-bottom-color: #3b3b3b;
    color: #6e7079;
  }
  .rp-table tbody tr {
    border-bottom-color: #3b3b3b;
    &:hover { background: rgba(59, 130, 246, 0.08); }
    &.is-total {
      background: linear-gradient(90deg, rgba(59, 130, 246, 0.12), rgba(249, 115, 22, 0.08));
      .td-num { color: #e4e6eb; }
    }
  }
  .rp-table td { color: #d0d2d6; }
  .pais-name { color: #d0d2d6; }
  .td-prev { color: #6e7079; }
}
</style>